import type { CalendarScheduleItem, EventCalendarDates } from './AddToCalendar.types';

const ISO_DAY = /^\d{4}-\d{2}-\d{2}/;

const toIsoTimestamp = (timestamp?: Maybe<string>): string | undefined => {
  if (!timestamp) {
    return undefined;
  }
  const date = new Date(timestamp);
  return isNaN(date.getTime()) ? undefined : date.toISOString();
};

/**
 * Converts an event date to a UTC ISO string normalized to midnight UTC, for use as an all-day entry.
 *
 * Calendar libraries read all-day events with `.getUTCDate()`, so a local-midnight value would land on
 * the previous or next day depending on the guest's timezone.
 *
 * Two input shapes reach this, and only one of them may be parsed:
 *  - ISO-8601 (`2027-06-12T16:00:00-04:00`) — the leading `YYYY-MM-DD` already *is* the event-local
 *    day, so it is sliced. Parsing would re-read that day in the guest's timezone and shift it, and a
 *    date-only ISO string parses as UTC midnight rather than local midnight.
 *  - a human date (`June 12, 2027`) — no timezone to lose; parses as local midnight, so local parts
 *    are the intended day.
 */
export const toAllDayCalendarDate = (value?: Maybe<string>): string | undefined => {
  if (!value) {
    return undefined;
  }

  const isoDay = ISO_DAY.exec(value)?.[0];
  if (isoDay) {
    return `${isoDay}T00:00:00.000Z`;
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return undefined;
  }
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
};

/**
 * Re-anchors a midnight-UTC all-day date for the ICS (Apple) renderer.
 *
 * datebook is internally inconsistent: Google and Outlook format all-day dates from UTC parts, while
 * its ICS branch formats from *local* parts. Handing the same midnight-UTC date to all three puts
 * Apple a day early for every guest west of UTC. Rebuilding it as local midnight of the same calendar
 * day makes the ICS output agree with the other two.
 */
export const toIcsAllDayStart = (date: Date): Date => new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

/**
 * Resolves the dates an "Add to Calendar" entry should carry for the event as a whole.
 *
 * The main schedule item is the only time the couple can actually edit, so it wins and is passed
 * through untouched — the same values the Schedule page hands to its own button, so both land on the
 * same calendar entry. The event date only carries a meaningful day (its time is a creation-time
 * default with no editor behind it), so it degrades to an all-day entry rather than publishing an
 * hour nobody chose.
 */
export const getEventCalendarDates = (args: Readonly<{ mainScheduleItem?: Maybe<CalendarScheduleItem>; eventDate?: Maybe<string> }>): EventCalendarDates | undefined => {
  const { mainScheduleItem, eventDate } = args;

  const scheduleStart = toIsoTimestamp(mainScheduleItem?.startTime?.timestamp);
  if (scheduleStart) {
    return {
      start: scheduleStart,
      end: toIsoTimestamp(mainScheduleItem?.endTime?.timestamp),
      allDay: false,
    };
  }

  const allDayStart = toAllDayCalendarDate(eventDate);
  return allDayStart ? { start: allDayStart, allDay: true } : undefined;
};
